import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull, Button } from "iuvity-ui-components/dist";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../datatable/TableHeader";
import { FilterMatchMode } from "primereact/api";
import { Column } from "iuvity-ui-components/dist/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { Actions } from "../datatable/Actions";
import { DialogProps } from "../dialog/CustomDialog";
import { ApplicationCreator } from "../applications/ApplicationCreator";
import { useApplications } from "../../hooks/useApplications";
import { useVersions } from "../../hooks/useVersions";
import { AppContext } from "../../context/index";
import { Tag } from "iuvity-ui-components/dist/tag";
import { capitalizeFirstLetter } from "../../utils";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import { formatStringToDate2 } from "../../helpers/formatDate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";


interface TenantDataTableActionsProps {
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const TenantDataTableActions: React.FC<TenantDataTableActionsProps> = ({
  auxModalController,
}) => {
  const {
    t,
    appState: { selectedTenant, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);
  const isDeleted = selectedTenant?.status == 'DELETED';

  const navigate = useNavigate();

  const {
    data: appsData,
    applicationAction,
    reloadFn,
  } = useApplications("DATA",  selectedTenant?.id);

  const { data: versionsData } = useVersions("DATA");

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        header: t("Applications.nameLabel"),
      },
      {
        field: "segment",
        header: t("Applications.segmentLabel"),
      },
      {
        field: "status",
        header: t("Applications.statusLabel"),
      },
      {
        field: "createdat",
        header: t("Tenants.createdatLabel"),
      },
    ];
  }, [t]);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onEdit = (app: any) => {
    setAppState((prev) => ({
      ...prev,
      selectedApplication: app,
    }));
    navigate("/applications/edit");
  };

  const actionDeleteFn = async (app: any) => {
    await applicationAction("DELETE", app.id);
    reloadFn();
  };

  const onDelete = (app: any) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Applications.deleteModalTitle"),
      content: `${t("Applications.deleteModalContent1")} "${app.name}" ${t(
        "Applications.deleteModalContent2"
      )}`,
      onConfirm: () => actionDeleteFn(app),
    }));
  };

  const formOptions = useMemo(() => {
    if (appsData && versionsData) {
      return {
        segments: appsData.reduce(function (result: any, element: any) {
          if (
            ![
              ...new Set(result.map((segmentData: any) => segmentData.value)),
            ].includes(element.segment)
          ) {
            result.push({
              label: element.segment,
              value: element.segment,
            });
          }
          return result;
        }, []),
        tenants: {
          label: selectedTenant?.name,
          value: selectedTenant?.id,
        },
        versions: versionsData.map((ver: any) => ({
          label: ver.name,
          value: ver.name,
        })).sort((a: any, b: any) => b.label.localeCompare(a.label)),
      };
    } else {
      return;
    }
  }, [appsData, selectedTenant, versionsData]);
  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={capitalizeFirstLetter(rowData.status)}
      severity={
        (['active', 'activo', 'activa'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['delete', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );
  const onClearFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    
    })
    setAppState((prev) => ({
      ...prev,
    }));
  };

  return (
    <div className="section_edit_app_env" >
      <p className="pt-3 mb-4"> {t("Tenants.descriptionApplicationLabel")}</p>
      {/* {!isDeleted&&<SubheaderActions
        className="pt-3 pb-3 px-3 mx-2"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-edit",
            label: t("Tenants.adminApplicationLabel"),
            style: {
              display: !permissiondInfoByRole.READ ? 'none' : undefined,
            },
            onClick: () =>
              navigate('/applications')
          },
        ])}
      />} */}

      <BoxFull className="dt-c mt-0 p-3">
        {appsData ? (
          <DataTable
            header={
              <TableHeader
                title={t("Tenants.viewApplications")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFilters}
              />
            }
            sortField="order"
            sortOrder={1}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Roles.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={filters}
            value={appsData
              .filter((app: any) => app?.tenantid === selectedTenant?.id)
              .map((app: any) => ({
                ...app,
                status: t(`Applications.${app.status.toLowerCase()}StatusLabel`),
                createdat: !!app?.createdat ? formatStringToDate2(app?.createdat, true, t) : null,
              }))}
            scrollable
            resizableColumns
          >
            {selectedColumns.map((col, idx) => {
              return (
                <Column
                  key={`${col.field}-${idx}`}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions" ? "center" : "left"}
                  headerStyle={col.field === "actions"
                    ? { justifyContent: "center" }
                    : undefined}
                  style={{
                    boxShadow: (col.field === "actions")
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                    maxWidth: (col.field === "actions") ? "132px" : undefined,
                    whiteSpace: "normal",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  body={(rowData) => {
                    if (col.field == 'status') {
                      return statusBodyTemplate(rowData)
                    }
                    return rowData[col.field]
                  }}
                />
              );
            })}
          </DataTable>
        ) : (
          <FormSkeleton length={3} />
        )}
      </BoxFull>

      {modalProps.visible && formOptions && (
        <ApplicationCreator
          options={formOptions}
          tenantid={selectedTenant?.id}
          createFn={applicationAction}
          reloadFn={reloadFn}
          modalController={setModalProps}
          auxModalController={auxModalController}
        />
      )}
    </div>
  );
};
